import { useMemo, useState } from 'react';
import SharedDialog from '../../../shared/shared-dialog';
import GroupTemplateAutocomplete from '../../../shared/group-template-autocomplete';
import {
  templateTableHeaderCellSx,
  templateTableRowCellSx,
  TEMPLATE_TABLE_MAX_HEIGHT,
  templateChangesAlertSx,
  templateUpdatedLabelColor,
} from '../../../shared/template-table-styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getGroupTemplateById,
  groupSecuritySettingsQuery,
  saveGroupSecuritySettings,
  saveGroupSettings,
} from '../../../api/users-api';
import { GroupBusinessSettings, GroupTemplate } from '../../../../types';
import {
  DiffSection,
  buildChangedBusinessSettings,
  computeSettingsDiff,
  haveTemplateSettingsChanged,
} from '../../../shared/group-settings.config';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { Messages } from '../../../utils/message-constants';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

type ApplyTemplateResult =
  | { status: 'applied' }
  | { status: 'templateChangedSinceReview'; freshTemplate: GroupTemplate }
  | { status: 'securitySettingsNotLoaded' };

export function useApplyGroupTemplatePermission(directoryRoles: string[]) {
  const canApplyTemplate = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.ApplyGroupTemplate.value
  );

  const disabledReason = canApplyTemplate
    ? undefined
    : Messages.Errors.Permission.Acting(DirectoryRoles.ApplyGroupTemplate.name);

  return { canApplyTemplate, disabledReason };
}

interface ApplyTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  groupId: string;
  groupSettings: GroupBusinessSettings;
  directoryRoles: string[];
  changeLogRoles: string[];
}

export default function ApplyTemplateDialog({
  open,
  handleClose,
  groupEmail,
  groupId,
  groupSettings,
  directoryRoles,
  changeLogRoles,
}: ApplyTemplateDialogProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [selectedTemplate, setSelectedTemplate] =
    useState<GroupTemplate | null>(null);
  const [activeTab, setActiveTab] =
    useState<DiffSection['name']>('Basic information');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { data: currentSecuritySettings, isPending: securitySettingsPending } =
    useQuery({
      ...groupSecuritySettingsQuery(groupEmail, groupId),
      enabled: open && !!groupId,
    });

  const currentSecurityQuery =
    currentSecuritySettings?.memberRestriction?.query ?? '';

  const diff = useMemo(() => {
    if (!selectedTemplate) return null;
    return computeSettingsDiff(
      groupSettings,
      selectedTemplate,
      currentSecurityQuery
    );
  }, [selectedTemplate, groupSettings, currentSecurityQuery]);

  const totalChanges =
    diff?.reduce((sum, section) => sum + section.changedCount, 0) ?? 0;
  const changedSectionCount =
    diff?.filter((section) => section.changedCount > 0).length ?? 0;

  const { canApplyTemplate } = useApplyGroupTemplatePermission(directoryRoles);
  const requiresChangeLog = isAbleToSeeUIElement(
    changeLogRoles,
    DirectoryRoles.ApplyGroupTemplate.value
  );

  const { mutate: applyTemplate, isPending: applying } = useMutation({
    mutationFn: async (logMessage: string): Promise<ApplyTemplateResult> => {
      if (!selectedTemplate) return { status: 'applied' };

      const freshTemplate = await getGroupTemplateById(selectedTemplate.id);
      if (haveTemplateSettingsChanged(selectedTemplate, freshTemplate)) {
        return { status: 'templateChangedSinceReview', freshTemplate };
      }

      const freshDiff = computeSettingsDiff(
        groupSettings,
        freshTemplate,
        currentSecurityQuery
      );

      const businessChanged = freshDiff.some(
        (section) => section.name !== 'Security' && section.changedCount > 0
      );
      if (businessChanged) {
        const newSettings = buildChangedBusinessSettings(
          groupSettings,
          freshTemplate,
          freshDiff
        );
        await saveGroupSettings(
          groupEmail,
          newSettings,
          groupSettings,
          logMessage
        );
      }

      const securityRow = freshDiff.find(
        (section) => section.name === 'Security'
      )?.rows[0];
      if (securityRow?.changed && !currentSecuritySettings) {
        return { status: 'securitySettingsNotLoaded' };
      }
      if (securityRow?.changed && currentSecuritySettings) {
        await saveGroupSecuritySettings({
          ...currentSecuritySettings,
          groupEmail,
          changeLogMessage: logMessage,
          memberRestriction: {
            ...currentSecuritySettings.memberRestriction,
            query: freshTemplate.securityQuery ?? '',
          },
        });
      }

      return { status: 'applied' };
    },
    onSuccess: (result) => {
      if (result.status === 'templateChangedSinceReview') {
        setSelectedTemplate(result.freshTemplate);
        setSnackbar({
          open: true,
          severity: 'warning',
          message: Messages.Info.GroupTemplateChangedSinceReview,
        });
        setOpenChangeLogDialog(false);
        return;
      }
      if (result.status === 'securitySettingsNotLoaded') {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: Messages.Info.GroupSecuritySettingsNotLoaded,
        });
        setOpenChangeLogDialog(false);
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupSettings, groupEmail],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupSecuritySettings, groupEmail],
      });
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Group template'),
      });
      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
      setSelectedTemplate(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'this group template'
        ),
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleApplyClick = () => {
    if (requiresChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      applyTemplate('');
    }
  };

  const activeSection = diff?.find((section) => section.name === activeTab);

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title="Apply Group Template"
        maxWidth="md"
        primaryButton={{
          action: handleApplyClick,
          title: `Apply Template${
            totalChanges > 0 ? ` (${totalChanges} Changes)` : ''
          }`,
          disabled:
            !selectedTemplate ||
            !canApplyTemplate ||
            applying ||
            securitySettingsPending ||
            totalChanges === 0,
          loading: applying,
        }}
        dialogContent={
          <>
            <Typography
              variant="body2"
              sx={{ mb: 2, color: theme.palette.semantic.text.title2 }}
            >
              Choose a template to apply to this group&apos;s settings.
            </Typography>
            <GroupTemplateAutocomplete
              value={selectedTemplate}
              onChange={(newValue) => {
                setSelectedTemplate(newValue);
                setActiveTab('Basic information');
              }}
              sx={{ mb: 3 }}
            />

            {selectedTemplate && diff && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Review changes before applying.
                </Typography>
                {totalChanges > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      boxSizing: 'border-box',
                      height: '36px',
                      padding: '8px 16px',
                      alignItems: 'center',
                      gap: 1,
                      alignSelf: 'stretch',
                      borderRadius: '4px',
                      ...templateChangesAlertSx(theme),
                      mb: 3,
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        width: '16.667px',
                        height: '16.667px',
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      <strong>
                        {totalChanges} setting{totalChanges === 1 ? '' : 's'}
                      </strong>{' '}
                      will be updated across {changedSectionCount} section
                      {changedSectionCount === 1 ? '' : 's'}.
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    This template matches the group&apos;s current settings.
                  </Alert>
                )}
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  scrollButtons="auto"
                  sx={{
                    mb: 4,
                    borderBottom: `1px solid ${theme.palette.semantic.outline.light}`,
                    '& .MuiTabs-flexContainer': { marginTop: '6px' },
                    '.MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
                    '& .MuiTabs-scrollButtons': {
                      '&[aria-disabled="true"]': {
                        display: 'none',
                      },
                    },
                  }}
                >
                  {diff.map((section) => (
                    <Tab
                      key={section.name}
                      value={section.name}
                      sx={{
                        minHeight: '40px',
                        height: '40px',
                        width: 'fit-content',
                      }}
                      label={
                        section.changedCount > 0
                          ? `${section.name} (${section.changedCount})`
                          : section.name
                      }
                    />
                  ))}
                </Tabs>
                <Box
                  sx={{
                    maxHeight: TEMPLATE_TABLE_MAX_HEIGHT,
                    overflow: 'auto',
                    flexShrink: 0,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Setting', 'Current Value', 'Template Value', ''].map(
                          (heading) => (
                            <TableCell
                              key={heading}
                              sx={templateTableHeaderCellSx(theme)}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  alignSelf: 'stretch',
                                }}
                              >
                                {heading}
                              </Box>
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeSection?.rows.map((row) => {
                        const cellSx = templateTableRowCellSx(theme);
                        return (
                          <TableRow key={row.field}>
                            <TableCell sx={cellSx}>{row.label}</TableCell>
                            <TableCell sx={cellSx}>
                              {row.currentValue}
                            </TableCell>
                            <TableCell sx={cellSx}>
                              {row.templateValue}
                            </TableCell>
                            <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                              {row.changed ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: templateUpdatedLabelColor(theme),
                                  }}
                                >
                                  Updated
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No change
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </>
        }
      />

      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => applyTemplate(changeLogMessage)}
          mutateLoading={applying}
        />
      )}
    </>
  );
}
